import React, { useCallback, useEffect, useRef } from 'react';

import {
  Box,
  Button,
  DateTimePickerField,
  FormCheckbox,
  FormDateField,
  FormField,
  FormNumberField,
  Loader,
  LoadingButton,
  MenuItem,
  PageHeader,
  TypeaheadFormField,
  Typography,
} from 'components';
import { useNotifications } from 'hooks';

import {
  FieldError,
  dayjs,
  useForm,
  useSelector,
  useTranslation,
  useWatch,
} from 'third-party';

import { DEFAULT_TIMEZONE } from 'constants/common';
import { SCHEDULE_INTERVAL } from 'constants/url';

import { useCache } from 'modules/cache';
import { usePayment } from 'modules/payment';

import { appSelectors, paymentSelectors, settingSelectors } from 'selectors';

import { convertFromUtcToTimezone, convertToUtcIsoString } from 'utils/app';
import { isValidCheckNumber, sanitizeCheckNumber } from 'utils/payment';

import {
  APIGenericList,
  APIPayee,
  APIPayment,
  APIPaymentRequest,
  APIPaymentStatus,
  APIResponse,
  APISettingName,
} from 'types/api';
import { TypeaheadOption } from 'types/app';

enum FormFields {
  Id = 'id',
  BankId = 'bankId',
  PayeeId = 'payeeId',
  Status = 'status',
  Amount = 'amount',
  CheckNumber = 'checkNumber',
  CheckDate = 'checkDate',
  Note = 'note',
  Memo = 'memo',
  Name = 'name',
  Company = 'company',
  NameOnCheck = 'nameOnCheck',
  ReferenceId = 'referenceId',
  Email = 'email',
  Phone = 'phone',
  Address = 'address',
  City = 'city',
  State = 'state',
  DeliveryTime = 'deliveryTime',
  Zip = 'zip',
  Kid = 'kId',
  OwnedByCaller = 'ownedByCaller',
}

type FormData = {
  [FormFields.Id]: string;
  [FormFields.BankId]: {
    label: string | null | undefined;
    value: string | undefined;
  } | null;
  [FormFields.PayeeId]: {
    label: string | null | undefined;
    value: string | undefined;
  } | null;
  [FormFields.Amount]: string;
  [FormFields.Status]: APIPaymentStatus;
  [FormFields.CheckNumber]: string;
  [FormFields.CheckDate]: string | null;
  [FormFields.Note]: string;
  [FormFields.Memo]: string;
  [FormFields.Name]: string;
  [FormFields.Company]: string;
  [FormFields.NameOnCheck]: string;
  [FormFields.ReferenceId]: string;
  [FormFields.Email]: string;
  [FormFields.Phone]: string;
  [FormFields.Address]: string;
  [FormFields.City]: string;
  [FormFields.State]: string;
  [FormFields.DeliveryTime]: string | null;
  [FormFields.Zip]: string;
  [FormFields.Kid]: string;
  [FormFields.OwnedByCaller]: boolean;
};

type CreateEditProps = {
  onModalClose: () => void;
  paymentId?: string | null;
};

export const PaymentCreateEditForm: React.FC<CreateEditProps> = ({
  paymentId,
  onModalClose,
}) => {
  const { loadPayment, clearPayment } = usePayment();

  const isEditMode = !!paymentId;
  const payment = useSelector(paymentSelectors.payment);

  useEffect(() => {
    if (paymentId) {
      loadPayment(paymentId);
    }
    return () => {
      clearPayment();
    };
  }, [paymentId, loadPayment, clearPayment]);

  if (isEditMode && !payment) {
    return <Loader />;
  }

  return (
    <CreateEditForm
      payment={isEditMode ? payment : null}
      onModalClose={onModalClose}
      isEditMode={isEditMode}
    />
  );
};

type Props = {
  payment: APIPayment | null;
  onModalClose: () => void;
  isEditMode: boolean;
};

export const StatusMenu = [
  <MenuItem key={APIPaymentStatus.Draft} value={APIPaymentStatus.Draft}>
    Draft
  </MenuItem>,
  <MenuItem key={APIPaymentStatus.Created} value={APIPaymentStatus.Created}>
    Created
  </MenuItem>,
  <MenuItem key={APIPaymentStatus.Scheduled} value={APIPaymentStatus.Scheduled}>
    Scheduled
  </MenuItem>,
];

export const CreateEditForm: React.FC<Props> = ({
  payment,
  onModalClose,
  isEditMode,
}) => {
  const { error, success } = useNotifications();
  const { updatePayment, createPayment } = usePayment();
  const { loadBankTypeahead, loadPayeeTypeahead } = useCache();
  const { t } = useTranslation();
  const createPaymentPending = useSelector(
    paymentSelectors.createPaymentPending,
  );
  const disabledEdit =
    (isEditMode && payment?.status !== APIPaymentStatus.Draft) ||
    createPaymentPending;

  const organizationId = useSelector(appSelectors.organizationId);
  const isAdminUser = useSelector(appSelectors.isAdminUser);

  const timezoneValue = useSelector(
    settingSelectors.getSettingValue(APISettingName.Timezone, DEFAULT_TIMEZONE),
  );

  const {
    handleSubmit,
    control,
    setValue,
    formState: { errors },
  } = useForm({
    mode: 'onChange',
    defaultValues: {
      [FormFields.Id]: payment?.id || '',
      [FormFields.BankId]: payment?.bank?.id
        ? {
            label: payment?.bank?.bankName,
            value: payment?.bank?.id,
          }
        : null,
      [FormFields.Amount]: payment?.amount?.toString() || '',
      [FormFields.CheckNumber]: payment?.checkNumber || '',
      [FormFields.CheckDate]:
        payment?.checkDate || dayjs().format('YYYY-MM-DD'),
      [FormFields.Note]: payment?.note || '',
      [FormFields.PayeeId]: payment?.payee?.id
        ? {
            label: payment?.payee?.nameOnCheck || '',
            value: payment?.payee?.id || '',
          }
        : null,
      [FormFields.Memo]: payment?.memo || '',
      [FormFields.Name]: payment?.name || '',
      [FormFields.Company]: payment?.company || '',
      [FormFields.Status]: payment?.status || APIPaymentStatus.Created,
      [FormFields.NameOnCheck]: payment?.payee?.nameOnCheck || '',
      [FormFields.ReferenceId]: payment?.referenceId || '',
      [FormFields.Email]: payment?.payee?.email || '',
      [FormFields.Phone]: payment?.payee?.phone || '',
      [FormFields.Address]: payment?.payee?.address || '',
      [FormFields.City]: payment?.payee?.city || '',
      [FormFields.State]: payment?.payee?.state || '',
      [FormFields.DeliveryTime]: convertFromUtcToTimezone(
        payment?.deliveryTime,
        timezoneValue,
      ),
      [FormFields.Zip]: payment?.payee?.zip || '',
      [FormFields.Kid]: payment?.kId || '',
      [FormFields.OwnedByCaller]: false,
    },
  });

  const watchedStatus = useWatch({ control, name: FormFields.Status });
  const watchedPayee = useWatch({ control, name: FormFields.PayeeId });
  const isPayeeEnteredByHand = !watchedPayee?.value;
  const previousStatus = useRef(watchedStatus);

  useEffect(() => {
    const wasScheduled = previousStatus.current === APIPaymentStatus.Scheduled;
    previousStatus.current = watchedStatus;

    // a delivery time only applies to a scheduled payment, so drop it as soon as
    // the payment is switched to another status
    if (wasScheduled && watchedStatus !== APIPaymentStatus.Scheduled) {
      setValue(FormFields.DeliveryTime, null, { shouldValidate: true });
    }
  }, [watchedStatus, setValue]);

  const isFormPending = false;

  const handleCancel = () => {
    onModalClose();
  };

  const onSubmit = async (formData: FormData) => {
    const formattedData = {
      ...formData,
      checkDate: dayjs(formData.checkDate).format('YYYY-MM-DD'),
      deliveryTime: convertToUtcIsoString(formData.deliveryTime, timezoneValue),
      bankId: formData.bankId?.value,
      payeeId: formData.payeeId?.value,
    };

    if (isEditMode && payment) {
      const result = await updatePayment(
        payment?.id,
        formattedData as any as APIPaymentRequest,
      );
      if (result.error) {
        error(result.payload.body?.title || t('paymentForm.edit.error'));
      } else {
        success(t('paymentForm.edit.sucscefull'));
        onModalClose();
      }
      return;
    }

    // create
    const result = await createPayment(
      formattedData as any as APIPaymentRequest,
    );
    if (result.error) {
      error(result.payload.body?.title || t('paymentForm.create.error'));
    } else {
      success(t('paymentForm.create.sucscefull'));
      onModalClose();
    }
  };

  const loadBank = async (value: string) => {
    const result: APIResponse<{ name: string; id: string }[]> =
      await loadBankTypeahead(organizationId, value);
    let options: TypeaheadOption[] = [];
    if (!result.error) {
      options = result.payload.map(it => ({
        label: it.name,
        value: it.id,
      }));
    }
    return {
      options,
    };
  };

  const loadPayees = async (value: string) => {
    const result: APIResponse<APIGenericList<APIPayee>> =
      await loadPayeeTypeahead(organizationId, value);

    let options: TypeaheadOption[] = [];
    if (!result.error) {
      options = result.payload?.result.map(it => {
        const data = [
          t(`payeeList.column.payeeStatus.${it.status}` as any),
          it.city,
          it.state,
          it.email,
        ]
          .filter(Boolean)
          .join(', ');

        return {
          label: `${it.nameOnCheck} (${data})`,
          value: it.id,
          ...it,
        };
      });
    }

    return {
      options,
    };
  };

  const onBankChange = useCallback(
    (option: TypeaheadOption | null) => {
      setValue(FormFields.BankId, option, {
        shouldValidate: true,
        shouldDirty: true,
      });
    },
    [setValue],
  );

  const onPayeeChange = useCallback(
    (option: APIPayee | null) => {
      setValue(FormFields.PayeeId, option as any, {
        shouldValidate: true,
        shouldDirty: true,
      });
      setValue(FormFields.NameOnCheck, option?.nameOnCheck || '');
      setValue(FormFields.Email, option?.email || '');
      setValue(FormFields.Phone, option?.phone || '');
      setValue(FormFields.Address, option?.address || '');
      setValue(FormFields.City, option?.city || '');
      setValue(FormFields.State, option?.state || '');
      setValue(FormFields.Zip, option?.zip || '');
    },
    [setValue],
  );

  return (
    <Box padding="1.5rem">
      <form>
        <PageHeader
          header={
            isEditMode
              ? t('paymentForm.edit.header')
              : t('paymentForm.create.header')
          }
          subHeader={
            isEditMode
              ? t('paymentForm.edit.subHeader')
              : t('paymentForm.create.subHeader')
          }
        >
          <Box display="flex" columnGap="1rem">
            <LoadingButton
              type="submit"
              variant="outlined"
              loading={createPaymentPending}
              onClick={handleSubmit(onSubmit)}
              size="medium"
              color="primary"
            >
              {t('common.form.save')}
            </LoadingButton>
            <Button
              type="button"
              disabled={createPaymentPending}
              variant="outlined"
              onClick={handleCancel}
              size="medium"
              color="error"
            >
              {t('common.form.cancel')}
            </Button>
          </Box>
        </PageHeader>
        <FormField
          type="select"
          fieldName={FormFields.Status}
          fieldError={errors[FormFields.Status] as FieldError}
          label={t('paymentList.column.status')}
          placeholder={
            t('common.form.enterValueFor') + t('paymentList.column.status')
          }
          selectOptions={StatusMenu}
          requiredErrorMessage={t('common.form.fieldRequired')}
          control={control}
          isRequired
          isDisabled={isFormPending}
        />
        <FormField
          fieldName={FormFields.BankId}
          label={t('paymentList.column.bank')}
          fieldError={errors[FormFields.BankId] as FieldError}
          requiredErrorMessage={t('common.form.emptyFieldError')}
          control={control}
          isRequired
          rules={{
            validate: {
              bankSelected: (value: { value?: string } | null) =>
                !!value?.value || t('common.form.emptyFieldError'),
            },
          }}
          render={() => (
            <TypeaheadFormField
              searchIcon
              hasError={!!errors[FormFields.BankId]}
              placeholder={
                t('common.form.typeToSearchValueFor') +
                t('paymentList.column.bank')
              }
              defaultValue={
                payment?.bank?.id
                  ? {
                      label: payment?.bank?.bankName ?? '',
                      value: payment?.bank?.id,
                    }
                  : null
              }
              loadOptions={loadBank}
              onChange={onBankChange}
              isDisabled={isFormPending || disabledEdit}
            />
          )}
        />
        <FormField
          fieldName={FormFields.PayeeId}
          label={t('paymentList.column.payee')}
          fieldError={errors[FormFields.PayeeId] as FieldError}
          requiredErrorMessage={t('common.form.emptyFieldError')}
          control={control}
          isRequired={false}
          render={() => (
            <TypeaheadFormField
              searchIcon
              placeholder={
                t('common.form.typeToSearchValueFor') +
                t('paymentList.column.payee')
              }
              defaultValue={
                payment
                  ? {
                      label: payment?.payee?.nameOnCheck ?? '',
                      value: payment?.payee?.id ?? '',
                    }
                  : null
              }
              loadOptions={loadPayees}
              onChange={onPayeeChange}
              isDisabled={isFormPending || disabledEdit}
            />
          )}
        />
        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary',
            display: '-webkit-box',
            fontSize: '0.7rem',
            overflow: 'hidden',
            WebkitBoxOrient: 'vertical',
            WebkitLineClamp: 2,
          }}
        >
          {t('paymentList.column.payee.helpText')}
        </Typography>
        <FormNumberField
          fieldName={FormFields.Amount}
          precision={2}
          fieldError={errors[FormFields.Amount] as FieldError}
          label={t('paymentList.column.amount')}
          placeholder={
            t('common.form.enterValueFor') + t('paymentList.column.amount')
          }
          requiredErrorMessage={t('common.form.fieldRequired')}
          control={control}
          maxLength={10}
          isRequired
          isDisabled={isFormPending || disabledEdit}
        />
        <FormField
          fieldName={FormFields.CheckNumber}
          fieldError={errors[FormFields.CheckNumber] as FieldError}
          label={t('paymentList.column.checkNumber')}
          placeholder={
            t('common.form.enterValueFor') + t('paymentList.column.checkNumber')
          }
          requiredErrorMessage={t('common.form.fieldRequired')}
          control={control}
          maxLength={100}
          inputProps={{ inputMode: 'numeric' }}
          rules={{
            validate: {
              wholeNumber: (value: string) =>
                !value ||
                isValidCheckNumber(value) ||
                t('common.form.invalidCheckNumber'),
            },
          }}
          onChange={event => {
            const sanitized = sanitizeCheckNumber(event.target.value);
            if (sanitized !== event.target.value) {
              setValue(FormFields.CheckNumber, sanitized, {
                shouldValidate: true,
              });
            }
          }}
          isRequired
          isDisabled={isFormPending}
        />
        <FormDateField
          isRequired
          showError
          fieldName={FormFields.CheckDate}
          label={t('paymentList.column.checkDate')}
          fieldError={errors[FormFields.CheckDate] as FieldError}
          requiredErrorMessage={t('common.form.emptyFieldError')}
          placeholder={
            t('common.form.enterValueFor') + t('paymentList.column.checkDate')
          }
          control={control}
          isDisabled={isFormPending || disabledEdit}
        />
        <DateTimePickerField
          fieldName={FormFields.DeliveryTime}
          label={t('paymentList.column.deliveryTime')}
          fieldError={errors[FormFields.DeliveryTime] as FieldError}
          requiredErrorMessage={t('common.form.emptyFieldError')}
          placeholder={
            t('common.form.enterValueFor') +
            t('paymentList.column.deliveryTime')
          }
          minutesStep={SCHEDULE_INTERVAL}
          control={control}
          isDisabled={
            isFormPending ||
            disabledEdit ||
            watchedStatus !== APIPaymentStatus.Scheduled
          }
          isRequired={false}
          rules={{
            required: {
              value: watchedStatus === APIPaymentStatus.Scheduled,
              message: t('common.form.fieldRequired'),
            },
          }}
        />
        <FormField
          fieldName={FormFields.Note}
          fieldError={errors[FormFields.Note] as FieldError}
          label={t('paymentList.column.note')}
          placeholder={
            t('common.form.enterValueFor') + t('paymentList.column.note')
          }
          requiredErrorMessage={t('common.form.fieldRequired')}
          control={control}
          maxLength={255}
          isRequired={false}
          isDisabled={isFormPending}
        />
        <FormField
          fieldName={FormFields.Memo}
          fieldError={errors[FormFields.Memo] as FieldError}
          label={t('paymentList.column.memo')}
          placeholder={
            t('common.form.enterValueFor') + t('paymentList.column.memo')
          }
          requiredErrorMessage={t('common.form.fieldRequired')}
          control={control}
          maxLength={255}
          isRequired={false}
          isDisabled={isFormPending || disabledEdit}
        />
        <FormField
          fieldName={FormFields.Name}
          fieldError={errors[FormFields.Name] as FieldError}
          label={t('paymentList.column.name')}
          placeholder={
            t('common.form.enterValueFor') + t('paymentList.column.name')
          }
          requiredErrorMessage={t('common.form.fieldRequired')}
          control={control}
          maxLength={100}
          isRequired={false}
          isDisabled={isFormPending || disabledEdit}
        />
        <FormField
          fieldName={FormFields.Company}
          fieldError={errors[FormFields.Company] as FieldError}
          label={t('paymentList.column.company')}
          placeholder={
            t('common.form.enterValueFor') + t('paymentList.column.company')
          }
          requiredErrorMessage={t('common.form.fieldRequired')}
          control={control}
          maxLength={100}
          isRequired={false}
          isDisabled={isFormPending || disabledEdit}
        />
        {isAdminUser && isPayeeEnteredByHand && !disabledEdit && (
          <FormCheckbox
            fieldName={FormFields.OwnedByCaller}
            fieldError={errors[FormFields.OwnedByCaller] as FieldError}
            title={t('payeeForm.ownedByCaller.title')}
            label={t('payeeForm.ownedByCaller.label')}
            labelVariant="caption"
            control={control}
            isDisabled={isFormPending}
          />
        )}
        <FormField
          fieldName={FormFields.NameOnCheck}
          fieldError={errors[FormFields.NameOnCheck] as FieldError}
          label={t('paymentList.column.nameOnCheck')}
          placeholder={
            t('common.form.enterValueFor') + t('paymentList.column.nameOnCheck')
          }
          requiredErrorMessage={t('common.form.fieldRequired')}
          control={control}
          maxLength={100}
          isRequired
          isDisabled={isFormPending || disabledEdit}
        />
        <FormField
          fieldName={FormFields.ReferenceId}
          fieldError={errors[FormFields.ReferenceId] as FieldError}
          label={t('paymentList.column.referenceId')}
          placeholder={
            t('common.form.enterValueFor') + t('paymentList.column.referenceId')
          }
          requiredErrorMessage={t('common.form.fieldRequired')}
          control={control}
          maxLength={100}
          isRequired={false}
          isDisabled={isFormPending || disabledEdit}
        />
        <FormField
          fieldName={FormFields.Email}
          fieldError={errors[FormFields.Email] as FieldError}
          label={t('paymentList.column.email')}
          placeholder={
            t('common.form.enterValueFor') + t('paymentList.column.email')
          }
          requiredErrorMessage={t('common.form.fieldRequired')}
          control={control}
          maxLength={100}
          isRequired
          isDisabled={isFormPending || disabledEdit}
        />
        <FormField
          fieldName={FormFields.Phone}
          fieldError={errors[FormFields.Phone] as FieldError}
          label={t('paymentList.column.phone')}
          placeholder={
            t('common.form.enterValueFor') + t('paymentList.column.phone')
          }
          requiredErrorMessage={t('common.form.fieldRequired')}
          control={control}
          maxLength={25}
          isRequired={false}
          isDisabled={isFormPending || disabledEdit}
        />
        <FormField
          fieldName={FormFields.City}
          fieldError={errors[FormFields.City] as FieldError}
          label={t('paymentList.column.city')}
          placeholder={
            t('common.form.enterValueFor') + t('paymentList.column.city')
          }
          requiredErrorMessage={t('common.form.fieldRequired')}
          control={control}
          maxLength={100}
          isRequired={false}
          isDisabled={isFormPending || disabledEdit}
        />
        <FormField
          fieldName={FormFields.Address}
          fieldError={errors[FormFields.Address] as FieldError}
          label={t('paymentList.column.address')}
          placeholder={
            t('common.form.enterValueFor') + t('paymentList.column.address')
          }
          requiredErrorMessage={t('common.form.fieldRequired')}
          control={control}
          maxLength={255}
          isRequired={false}
          isDisabled={isFormPending || disabledEdit}
        />
        <FormField
          fieldName={FormFields.State}
          fieldError={errors[FormFields.State] as FieldError}
          label={t('paymentList.column.state')}
          placeholder={
            t('common.form.enterValueFor') + t('paymentList.column.state')
          }
          requiredErrorMessage={t('common.form.fieldRequired')}
          control={control}
          maxLength={2}
          isRequired={false}
          isDisabled={isFormPending || disabledEdit}
        />
        <FormField
          fieldName={FormFields.Zip}
          fieldError={errors[FormFields.Zip] as FieldError}
          label={t('paymentList.column.zip')}
          placeholder={
            t('common.form.enterValueFor') + t('paymentList.column.zip')
          }
          requiredErrorMessage={t('common.form.fieldRequired')}
          control={control}
          maxLength={25}
          isRequired={false}
          isDisabled={isFormPending || disabledEdit}
        />
        <FormField
          fieldName={FormFields.Kid}
          fieldError={errors[FormFields.Kid] as FieldError}
          label={t('paymentList.column.kId')}
          placeholder={
            t('common.form.enterValueFor') + t('paymentList.column.kId')
          }
          requiredErrorMessage={t('common.form.fieldRequired')}
          control={control}
          maxLength={100}
          isRequired={false}
          isDisabled={isFormPending || disabledEdit}
        />
      </form>
    </Box>
  );
};
